import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isCognitoConfigured, cognitoConfig } from '../config/cognito';
import analytics from '../lib/tracking';

export default function SignUpPage() {
  const { signInWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const isConfigured = isCognitoConfigured();

  // Capture UTM parameters on mount
  useEffect(() => {
    const utmParams = {
      utm_source: searchParams.get('utm_source'),
      utm_medium: searchParams.get('utm_medium'),
      utm_campaign: searchParams.get('utm_campaign'),
      utm_term: searchParams.get('utm_term'),
      utm_content: searchParams.get('utm_content'),
    };

    // Store UTM params if any exist
    const hasUtmParams = Object.values(utmParams).some(value => value !== null);
    if (hasUtmParams) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
      console.log('Captured UTM parameters:', utmParams);
    }

    // Track page view
    analytics.pageView('/signup', 'Sign Up');
  }, [searchParams]);

  const handleGoogleSignIn = () => {
    // Track sign up button click
    analytics.engagement('google_signin_click', 'auth', {
      page: 'signup'
    });

    // Redirect to Cognito hosted UI
    signInWithGoogle();
  };

  // Show configuration help for local development
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-2xl mx-auto border-2 border-yellow-400 text-left">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">⚙️</div>
            <h1 className="text-text-dark text-2xl font-semibold mb-2">
              Cognito Configuration Required
            </h1>
            <p className="text-text-medium text-sm">
              Google Sign-In requires Cognito environment variables for local development.
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="text-sm font-semibold mb-2 text-gray-700">Create a .env file in frontend/main/:</h3>
            <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto">
{`VITE_COGNITO_USER_POOL_ID=${cognitoConfig.userPoolId || 'us-east-1_XXX'}
VITE_COGNITO_CLIENT_ID=${cognitoConfig.clientId || 'xxx'}
VITE_COGNITO_REGION=${cognitoConfig.region}
VITE_COGNITO_DOMAIN=${cognitoConfig.domain || 'daddy-time-staging-auth'}`}
            </pre>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">💡 Get values from Terraform:</h3>
            <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto">
cd terraform/environments/staging
terraform output -json | jq '.user_pool_id.value, .user_pool_client_id.value, .cognito_region.value, .cognito_domain.value'
            </pre>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              After creating the .env file, restart the dev server
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-app-bg px-4 pt-8 pb-20">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-auto border-2 border-accent-pink text-center">
        {/* Header */}
        <div className="text-6xl mb-6">🎉</div>
        <h1 className="text-text-dark text-2xl font-semibold mb-4">
          Welcome to Daddy Time
        </h1>
        <p className="text-text-medium text-sm leading-relaxed mb-8">
          Nurturing ASMR boyfriend meets meditation app. Continue with Google to get started — we'll create your account if you're new.
        </p>

        {/* Google Sign-In Button */}
        <button
          onClick={handleGoogleSignIn}
          className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-4 px-6 rounded-xl border-2 border-gray-300 flex items-center justify-center gap-3 transition-all active:scale-95"
        >
          {/* Google Logo SVG */}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.6 10.2273C19.6 9.51825 19.5364 8.83643 19.4182 8.18188H10V12.0501H15.3818C15.15 13.3001 14.4455 14.3592 13.3864 15.0682V17.5773H16.6182C18.5091 15.8364 19.6 13.2728 19.6 10.2273Z" fill="#4285F4"/>
            <path d="M10 20C12.7 20 14.9636 19.1045 16.6182 17.5773L13.3864 15.0682C12.4909 15.6682 11.3455 16.0227 10 16.0227C7.39545 16.0227 5.19091 14.2636 4.40455 11.9H1.06364V14.4909C2.70909 17.7591 6.09091 20 10 20Z" fill="#34A853"/>
            <path d="M4.40455 11.9C4.20455 11.3 4.09091 10.6591 4.09091 10C4.09091 9.34091 4.20455 8.7 4.40455 8.1V5.50909H1.06364C0.386364 6.85909 0 8.38636 0 10C0 11.6136 0.386364 13.1409 1.06364 14.4909L4.40455 11.9Z" fill="#FBBC04"/>
            <path d="M10 3.97727C11.4682 3.97727 12.7864 4.48182 13.8227 5.47273L16.6909 2.60455C14.9591 0.990909 12.6955 0 10 0C6.09091 0 2.70909 2.24091 1.06364 5.50909L4.40455 8.1C5.19091 5.73636 7.39545 3.97727 10 3.97727Z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Terms */}
        <p className="text-text-medium text-xs mt-6">
          By signing in, you agree to our terms of service and privacy policy.
        </p>
      </div>
    </div>
  );
}
